import { useUserService } from 'user/services/useUserService';
import { createContext, useEffect, useState } from 'react';
import { useDataFetcher } from 'shared/http';
import { Usuario } from 'user/models/Usuario';
import { useNavigate, useParams } from 'react-router-dom';
import { UserRequest } from 'user/models/UserRequest';
import { useNotifications } from 'shared/notifications';
import { USER_CRUD } from 'user/constants/User';
import { LIST_USERS_PATH } from 'user/routes';

export interface SaveUserApiErrors {
  cost_centre_not_found: boolean;
  card_not_found: boolean;
  user_exist: boolean;
  invalid_email: boolean;
}

interface IUserInfoContext {
  isCreationPage: boolean;
  userInfo: Usuario;
  isLoading: boolean;
  saveUser: (userRequest: UserRequest) => void;
  apiErrors: SaveUserApiErrors;
}

const DEFAULT_API_ERRORS = {
  cost_centre_not_found: false,
  card_not_found: false,
  invalid_email: false,
  user_exist: false,
};

export const UserInfoContext = createContext<IUserInfoContext>({
  isCreationPage: false,
  userInfo: null,
  isLoading: true,
  saveUser: () => null,
  apiErrors: DEFAULT_API_ERRORS,
});

export const UserInfoProvider = ({ children }) => {
  const [userInfo, setRequest] = useDataFetcher<Usuario>();
  let { userId } = useParams();
  const userService = useUserService();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [apiErrors, setApiErrors] = useState(DEFAULT_API_ERRORS);

  useEffect(() => {
    if (userId) {
      setRequest(() => userService.getUser(userId));
    }
  }, [setRequest, userService, userId]);

  const contextValue = {
    userInfo: userInfo.data || null,
    isLoading: userInfo.isLoading || isSaving,
    saveUser: (userRequest: UserRequest) => {
      setIsSaving(true);
      userService
        .saveUser(userId, userRequest)
        .then(() => {
          addNotification(USER_CRUD.updated, 'success');
          navigate(LIST_USERS_PATH);
        })
        .catch((error) => {
          const errorKey = error.apiError.message;

          if (errorKey in DEFAULT_API_ERRORS) {
            setApiErrors({ ...DEFAULT_API_ERRORS, [errorKey]: true });
          } else {
            // Error no manejado
            addNotification('Hubo un error al intentar guardar el usuario', 'error');
            setApiErrors(DEFAULT_API_ERRORS);
          }
        })
        .finally(() => setIsSaving(false));
    },
    apiErrors,
    isCreationPage: !userId,
  };

  return (
    <UserInfoContext.Provider value={contextValue}>{children}</UserInfoContext.Provider>
  );
};
